import React, {useEffect, useRef, useState} from "react"
import CompleteUserAdminRegistration, {
    CompanyPropsForRegistration,
    UserPropsForRegistration
} from "../../components/registration/complete-user-admin-registration/CompleteUserAdminRegistration";
import {useAppSelector} from "../../redux/reducer/store";
import {
    apiSlice,
    useCreateAgencyMutation,
    useInviteUsersMutation,
    useUpdateCompanyMutation,
    useUpdateUserInfoSelfMutation
} from "../../redux/reducer/api";
import {GENERAL_BACKEND_RESPONSE} from "../../api/model/common";
import ErrorPage from "../functional/error-page/ErrorPage";
import {CompanyProps} from "../../api/model/company";
import {getCompany} from "../../utils/getter/agencies-company";
import CreateInitialAgencyAndInviteUsers
    from "../../components/registration/create-initial-agency-and-invite-users/CreateInitialAgencyAndInviteUsers";
import {CreateAgencyRequest} from "../../api/model/agency";
import {toast} from "react-toastify";
import ToastWithTextOnly from "../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {UserToInviteProps} from "../../api/model/user";
import FullscreenSpinner from "../../molecules/fullscreen-spinner/FullscreenSpinner";
import {PATH_STRINGS} from "../../routes/route-props";
import {Navigate} from "react-router-dom";

const CompleteRegistration = () => {
    const {userData, companies} = useAppSelector(state => state.user)
    const [userInfo, setUserInfo] = useState(userData)
    const [page, setPage] = useState(0)
    const [companyInfo, setCompanyInfo] = useState<CompanyProps | null>(null)
    const usersToInvite = useRef<UserToInviteProps[] | null>(null)

    const [triggerGetUserInfoBackup, {
        data: userInfoBackupResponse,
        isLoading: isLoadingUserInfoBackup,
        error: userInfoErrorBackup
    }] = apiSlice.endpoints?.getUserInfoSelf.useLazyQuery()

    const [triggerGetCompanyInfoBackup, {
        data: companyInfoBackupResponse,
        isLoading: loadingCompanyInfoBackup,
        error: companyInfoErrorBackup
    }] = apiSlice.endpoints?.getUsersCompany.useLazyQuery()

    const [updateUserInfo, {
        data: updateUserInfoResponse,
        error: isErrorUpdateUserInfo,
    }] = useUpdateUserInfoSelfMutation()

    const [updateCompany, {
        data: updateCompanyResponse,
        isError: errorUpdateCompanyRequest
    }] = useUpdateCompanyMutation()

    const [createAgency, {
        data: createAgencyResponse,
        isLoading: loadingCreateAgencyRequest,
        isError: errorCreateAgencyRequest,
    }] = useCreateAgencyMutation()

    const [inviteUsersRequest, {
        isLoading: loadingInviteUser,
        isError: errorInviteUser
    }] = useInviteUsersMutation()

    useEffect(() => {
        if (companies && userInfo) setCompanyInfo(getCompany(userInfo.isKlimaLink, userInfo.companyId, companies))
    }, []);

    useEffect(() => {
        if (!userData) triggerGetUserInfoBackup()
    }, []);

    useEffect(() => {
        if (!companies && userInfoBackupResponse) triggerGetCompanyInfoBackup()
    }, [userInfoBackupResponse]);

    useEffect(() => {
        if (userInfoBackupResponse) setUserInfo(userInfoBackupResponse)
    }, [userInfoBackupResponse]);

    useEffect(() => {
        if (companyInfoBackupResponse && userInfoBackupResponse) setCompanyInfo(getCompany(userInfoBackupResponse.isKlimaLink, userInfoBackupResponse.companyId, companyInfoBackupResponse))
    }, [companyInfoBackupResponse]);

    useEffect(() => {
        if (companyInfo?.isAdmin && page === 0) {
            if (
                updateUserInfoResponse?.status === GENERAL_BACKEND_RESPONSE.success &&
                updateCompanyResponse?.status === GENERAL_BACKEND_RESPONSE.success
            ) setPage(page + 1)
        } else {
            if (updateUserInfoResponse?.status === GENERAL_BACKEND_RESPONSE.success) {
                if (usersToInvite.current && usersToInvite.current?.length > 0 && userInfo && userInfo.agencyIds[0] !== undefined) inviteUsersRequest({
                    users: usersToInvite.current,
                    agencyId: userInfo.agencyIds[0]
                })
                toast(
                    <ToastWithTextOnly
                        message={"SCREENS.REGISTRATION.REGISTRATION_COMPLETE_MESSAGE"}/>,
                    {progress: undefined, type: "success"}
                )
                triggerGetUserInfoBackup()
            }
        }
    }, [updateUserInfoResponse, updateCompanyResponse]);

    useEffect(() => {
        if (createAgencyResponse?.status === GENERAL_BACKEND_RESPONSE.success) {
            toast(<ToastWithTextOnly
                    message={"TOASTS.SUCCESS.ADD_AGENCY"}/>,
                {progress: undefined, type: "success"}
            );
            updateUserInfo({registrationComplete: true})
        }
    }, [createAgencyResponse]);

    useEffect(() => {
        if (errorCreateAgencyRequest) {
            toast(<ToastWithTextOnly
                    message={"ERRORS.FAILED_CREATE_AGENCY_UPON_REGISTRATION"}/>,
                {progress: undefined, type: "error"}
            );
            updateUserInfo({registrationComplete: true})
        }
    }, [errorCreateAgencyRequest]);

    useEffect(() => {
        if (errorInviteUser) {
            toast(<ToastWithTextOnly
                    message={"SCREENS.CREATE_AGENCY_INVITE_USERS.COMPLETE_FAIL_INVITE_USER"}/>,
                {progress: undefined, type: "error"}
            );
            updateUserInfo({registrationComplete: true})
        }
    }, [errorInviteUser]);

    const _completeUserAdminRegistration = (providedUserData: UserPropsForRegistration, providedCompanyData?: CompanyPropsForRegistration) => {
        if (providedCompanyData && userInfo && companyInfo) {
            updateCompany({
                name: companyInfo?.name,
                ...providedCompanyData
            })
            updateUserInfo(providedUserData)
        } else {
            updateUserInfo(providedUserData)
        }
    }

    // Users can only be invited to an agency if the agency already exits. Because of that, the agency is created before the users are invited.
    // After creating agency and inviting users, the registration for the admin user is completed. If there are no users to invite, the registration is completed
    // after the agency is created.
    const _onCreateAgencyAndInviteUsers = (users: UserToInviteProps[], agency: CreateAgencyRequest) => {
        usersToInvite.current = users
        createAgency(agency)
        if (!users.some(user => user.firstName.length > 0 && user.lastName.length > 0 && user.email.length > 0)) updateUserInfo({registrationComplete: true})
    }

    const _chooseScreen = () => {
        if (companyInfo && userInfo) {
            if (page === 0) return <CompleteUserAdminRegistration
                onRegister={(user, company) => _completeUserAdminRegistration(user, company)}
                company={companyInfo}
                user={userInfo}
                isCompanyAdmin={Boolean(companyInfo.isAdmin)}/>
            if (page === 1) return <CreateInitialAgencyAndInviteUsers
                onCreateAgencyInviteUsers={(users, agency) => _onCreateAgencyAndInviteUsers(users, agency)}
                onSkip={() => updateUserInfo({registrationComplete: true})}
                onBack={() => setPage(page - 1)}
                agencyAdmin={userInfo}/>
        }
        return null
    }

    if (userInfoErrorBackup || companyInfoErrorBackup) return <ErrorPage/>
    if (isErrorUpdateUserInfo || errorUpdateCompanyRequest) return <ErrorPage
        additionalText={"ERRORS.REGISTRATION_UPDATE_USER_INFO"}/>
    if (loadingCreateAgencyRequest || loadingInviteUser) return <FullscreenSpinner
        showLogo={false}
        message={loadingCreateAgencyRequest ? "SCREENS.REGISTRATION.CREATING_AGENCY_MESSAGE" : "SCREENS.REGISTRATION.INVITING_USERS_MESSAGE"}
        transparent={true}/>
    if (isLoadingUserInfoBackup || loadingCompanyInfoBackup) return <FullscreenSpinner
        showLogo={true}/>
    if (userInfo?.registrationComplete) return <Navigate
        to={PATH_STRINGS.travelDatabase}/>
    if (userInfo && companyInfo) return _chooseScreen()
    return <ErrorPage/>
}

export default CompleteRegistration